import { Redirect } from "@redotech/react-router-util/redirect";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { oauthRoutes } from "@redotech/redo-merchant-login-app/oauth/route";
import { loginRoutes } from "@redotech/redo-merchant-login-app/route";
import {
  ConversionPermissions,
  CustomersPermissions,
  ImsPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
  SupportPermissions,
} from "@redotech/redo-model/permissions";
import { Navigate } from "react-router-dom";
import { aeoRoutes } from "./app/navigation/routing/aeo-routes";
import { conversionRoutes } from "./app/navigation/routing/conversion-routes";
import { customerPortalBuilderRoutes } from "./app/navigation/routing/customer-portal-builder-routes";
import { customersRoutes } from "./app/navigation/routing/customers-routes";
import { financeRoutes } from "./app/navigation/routing/finance-routes";
import {
  backwardsCompatibleHomeRoutes,
  homeRoutes,
} from "./app/navigation/routing/home-routes";
import { inventoryRoutes } from "./app/navigation/routing/inventory-routes";
import {
  backwardsCompatibleMarketingRoutes,
  marketingRoutes,
} from "./app/navigation/routing/marketing-routes";
import { messagingRoutes } from "./app/navigation/routing/messaging-routes";
import {
  backwardsCompatibleOrderManagementRoutes,
  orderManagementRoutes,
} from "./app/navigation/routing/order-management-routes";
import { redoSuppliesRoutes } from "./app/navigation/routing/redo-supplies-routes";
import { settingRoutes } from "./app/navigation/routing/settings-routes";
import {
  backwardsCompatibleSupportRoutes,
  supportRoutes,
} from "./app/navigation/routing/support-routes";
import { supportV2Routes } from "./app/navigation/routing/support-v2-routes";
import { approvalRoutes } from "./approvals/route";
import { bigcommerceRoutes } from "./bigcommerce/route";
import {
  marketingCommentSoldRoutes,
  omsCommentSoldRoutes,
} from "./commentsold/route";
import { faireRoutes } from "./faire/route";
import { gorgiasRoutes } from "./gorgias/route";
import { integrationsRoutes } from "./integrations/route";
import { paypalRoutes } from "./paypal/route";
import { MerchantApp404Page } from "./platform/merchant-app-404-page";
import { riseRoutes } from "./rise.ai/route";
import { RouterRoot } from "./root";
import { shopifyRoutes } from "./shopify/route";
import "./styles.css";
import { tiktokRoutes } from "./tiktok/route";
import { uspsRoutes } from "./usps/route";
import { walmartRoutes } from "./walmart/route";

const children: PermissionedRouteObject[] = [
  { index: true, element: <Navigate to="home" /> },
  { path: "home", children: homeRoutes },
  ...backwardsCompatibleHomeRoutes,
  { path: "aeo", children: aeoRoutes },
  {
    path: "support",
    children: supportRoutes,
    handle: { permission: SupportPermissions.VIEW_SUPPORT },
  },
  {
    path: "support-v2",
    children: supportV2Routes,
    handle: { permission: SupportPermissions.VIEW_SUPPORT },
  },
  ...backwardsCompatibleSupportRoutes,
  {
    path: "marketing",
    children: marketingRoutes,
    handle: { permission: MarketingPermissions.VIEW_MARKETING },
  },
  ...backwardsCompatibleMarketingRoutes,

  { path: "messaging", children: messagingRoutes },

  {
    path: "conversion",
    children: conversionRoutes,
    handle: { permission: ConversionPermissions.ACCESS_CONVERSION },
  },

  {
    path: "order-management",
    children: orderManagementRoutes,
    handle: {
      permission: [
        OrderManagementPermissions.MANAGE_WARRANTY,
        OrderManagementPermissions.VIEW_RETURN,
        OrderManagementPermissions.VIEW_ORDER,
        OrderManagementPermissions.SCAN_AND_VERIFY,
        OrderManagementPermissions.MANAGE_SHIPMENTS,
        OrderManagementPermissions.MANAGE_CARRIER_CLAIM,
        OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
        OrderManagementPermissions.MANAGE_PICK_SESSIONS,
      ],
    },
  },
  {
    path: "customer",
    children: customersRoutes,
    handle: { permission: CustomersPermissions.VIEW_CUSTOMER_RECORD },
  },
  { path: "finance", children: financeRoutes },
  { path: "redo-supplies", children: redoSuppliesRoutes },
  ...backwardsCompatibleOrderManagementRoutes,

  {
    path: "inventory",
    children: inventoryRoutes,
    handle: { permission: Object.values(ImsPermissions) },
  },
  {
    path: "order-management/approvals",
    children: approvalRoutes,
    handle: { permission: OrderManagementPermissions.VIEW_RETURN },
  },
  { path: "settings", children: settingRoutes },
  { path: "customer-portal-builder", children: customerPortalBuilderRoutes },
  { path: "integrations", children: integrationsRoutes },

  { path: "login", children: loginRoutes },
  { path: "oauth", children: oauthRoutes },
  { path: "shopify", children: shopifyRoutes },
  { path: "bigcommerce", children: bigcommerceRoutes },
  {
    path: "gorgias/*",
    element: <Redirect match={/gorgias/} replaceValue="support/gorgias" />,
  },
  { path: "gorgias", children: gorgiasRoutes },
  { path: "rise-ai", children: riseRoutes },
  {
    path: "tariff-calculator",
    async lazy() {
      const { PublicTariffCalculator: TariffCalculator } = await import(
        "./public/tariff-calculator"
      );
      return { Component: TariffCalculator };
    },
  },
  { path: "tiktok", children: tiktokRoutes },
  { path: "walmart", children: walmartRoutes },
  { path: "oms-commentsold", children: omsCommentSoldRoutes },
  { path: "marketing-commentsold", children: marketingCommentSoldRoutes },
  { path: "usps", children: uspsRoutes },
  { path: "faire", children: faireRoutes },
  { path: "paypal", children: paypalRoutes },
];

export const routes: PermissionedRouteObject[] = [
  {
    path: "/stores/:teamId/",
    Component: RouterRoot,
    children,
    errorElement: <MerchantApp404Page />,
  },
  {
    path: "/",
    Component: RouterRoot,
    errorElement: <MerchantApp404Page />,
    children,
  },
];
